import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/auth';
import { ModelReliability, performanceApi } from '../services/performanceApi';
import { SectionTitle, StatCard, fmtBucket, fmtNum, fmtPct, fmtS, healthColor } from '../components/performance/common';

const COLORS = ['#1976d2', '#e53935', '#43a047', '#fb8c00', '#8e24aa', '#00897b'];
const CHART_MODELS = 6;

export const ModelStatus: React.FC = () => {
  const { getIdToken, permissions } = useAuth();
  const api = useMemo(() => performanceApi(getIdToken), [getIdToken]);
  const [searchParams] = useSearchParams();
  const isAdmin = isAdminUser(permissions?.userType);
  const customerId = isAdmin ? searchParams.get('customer_id') : null;

  const [days, setDays] = useState(7);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [data, setData] = useState<ModelReliability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .models(scope === 'all' ? Math.min(days, 14) : days, scope, customerId)
      .then(d => !cancelled && setData(d))
      .catch(e => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load models'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, days, scope, customerId, reload]);

  const used = useMemo(() => (data?.models ?? []).filter(m => m.calls > 0), [data]);
  const chartModels = useMemo(() => used.slice(0, CHART_MODELS).map(m => m.model), [used]);
  const chartRows = useMemo(() => {
    if (!data) return [];
    const byBucket = new Map<string, Record<string, number | string | null>>();
    data.series.forEach(r => {
      if (!chartModels.includes(r.model)) return;
      const row = byBucket.get(r.bucket) ?? { label: fmtBucket(r.bucket, data.window.unit) };
      row[r.model] = r.calls ? Math.round(((r.calls - r.ok) / r.calls) * 1000) / 10 : null;
      byBucket.set(r.bucket, row);
    });
    return Array.from(byBucket.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [data, chartModels]);

  const unhealthy = (data?.models ?? []).filter(m => m.live && m.live.state !== 'healthy');
  const incidents = (data?.models ?? [])
    .flatMap(m => m.incidents.map(i => ({ ...i, model: m.model })))
    .sort((a, b) => b.start.localeCompare(a.start));
  const totals = used.reduce(
    (acc, m) => ({ calls: acc.calls + m.calls, ok: acc.ok + m.ok, timeouts: acc.timeouts + (m.timeouts || 0) }),
    { calls: 0, ok: 0, timeouts: 0 }
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4">Model Status</Typography>
          <Typography variant="body2" color="text.secondary">
            How the AI models behind your functions are performing: live health, failures by type, and past incidents.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin && (
            <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && setScope(v)}>
              <ToggleButton value="mine">{customerId ? 'This customer' : 'My runs'}</ToggleButton>
              <ToggleButton value="all">All customers</ToggleButton>
            </ToggleButtonGroup>
          )}
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
            {[1, 7, 14, 30].map(d => (
              <ToggleButton key={d} value={d} disabled={scope === 'all' && d > 14}>
                {d === 1 ? '24h' : `${d}d`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton onClick={() => setReload(r => r + 1)} disabled={loading}>
            {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <Box sx={{ opacity: loading ? 0.6 : 1 }}>
          {unhealthy.length > 0 ? (
            <Alert severity={unhealthy.some(m => m.live?.state === 'down') ? 'error' : 'warning'} sx={{ mb: 2 }}>
              {unhealthy.map(m => `${m.model} is ${m.live?.state}${m.live?.reason ? ` (${m.live.reason})` : ''}`).join(' · ')}. Functions
              with a backup model set will switch automatically.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              All models you use are healthy right now.
            </Alert>
          )}
          {!data.health_history_available && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Incident history is recorded once a minute from now on, so the incident list fills in over time.
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Model calls" value={fmtNum(totals.calls)} caption="Includes retries and backup attempts" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Calls that succeeded" value={fmtPct(totals.calls ? totals.ok / totals.calls : null)} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Calls that timed out" value={fmtNum(totals.timeouts)} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Incidents" value={fmtNum(incidents.length)} caption={`Degraded or down periods, last ${data.window.days}d`} />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <SectionTitle title="Models" help="Per model call, so a failed first attempt that a backup recovered from still counts here." />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell>Now</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Timeouts</TableCell>
                    <TableCell align="right">Rate limited</TableCell>
                    <TableCell align="right">Provider errors</TableCell>
                    <TableCell align="right">Invalid output</TableCell>
                    <TableCell align="right">p50 / p95</TableCell>
                    <TableCell>Affected now</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.models.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No model calls in this window
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.models.map(m => (
                      <TableRow key={m.model} hover>
                        <TableCell>{m.model}</TableCell>
                        <TableCell>
                          <Tooltip title={m.live?.reason || ''}>
                            <Chip size="small" label={m.live?.state ?? 'no recent calls'} color={healthColor(m.live?.state)} variant={m.live ? 'filled' : 'outlined'} />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">{fmtNum(m.calls)}</TableCell>
                        <TableCell align="right">{fmtPct(m.failure_rate)}</TableCell>
                        <TableCell align="right">{fmtNum(m.timeouts)}</TableCell>
                        <TableCell align="right">{fmtNum(m.rate_limited)}</TableCell>
                        <TableCell align="right">{fmtNum(m.provider_errors)}</TableCell>
                        <TableCell align="right">{fmtNum(m.bad_output)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {fmtS(m.p50_s)} / {fmtS(m.p95_s)}
                        </TableCell>
                        <TableCell>
                          {m.affected_functions.length ? (
                            <Tooltip title={m.affected_functions.map(f => `${f.function_name} (${f.runs_24h} runs/24h)`).join(', ')}>
                              <Chip size="small" color="warning" variant="outlined" label={`${m.affected_functions.length} function${m.affected_functions.length > 1 ? 's' : ''}`} />
                            </Tooltip>
                          ) : (
                            '–'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <SectionTitle title="Failure rate over time" help={`Share of calls that failed, ${data.window.unit === 'hour' ? 'per hour' : 'per day'}. Busiest ${CHART_MODELS} models.`} />
                <Box sx={{ height: 260 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartRows} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                      <YAxis tick={{ fontSize: 11 }} unit="%" />
                      <ChartTooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {chartModels.map((m, i) => (
                        <Line key={m} dataKey={m} stroke={COLORS[i % COLORS.length]} dot={false} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <SectionTitle title="Incidents" help="Periods a model was degraded or down (all customers' traffic)." />
                {incidents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No incidents recorded in this window.
                  </Typography>
                ) : (
                  <Stack spacing={1} sx={{ maxHeight: 260, overflow: 'auto' }}>
                    {incidents.slice(0, 50).map((i, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Chip size="small" label={i.ongoing ? `${i.state} now` : i.state} color={healthColor(i.state)} sx={{ minWidth: 80 }} />
                        <Box>
                          <Typography variant="body2">
                            {i.model} · {i.minutes} min
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(i.start).toLocaleString()}
                            {i.reasons.length ? ` · ${i.reasons.join('; ')}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ModelStatus;
